import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Card,
    Input,
    Button,
    Space,
    Typography,
    Row,
    Col,
    Statistic,
    message,
    Tag,
    Descriptions,
    Divider,
    Tabs,
    DatePicker
} from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
    FileTextOutlined,
    BankOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    EyeOutlined,
    DownloadOutlined,
    CloudSyncOutlined
} from '@ant-design/icons';
import adminApi from '../services/adminApi';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface Company {
    id: number;
    name: string;
    bin: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    directorIin?: string;
    bankName?: string;
    bankBik?: string;
    bankAccount?: string;
    bankKbe?: string;
    status: string;
    createdAt: string;
    updatedAt?: string;
    contractsCount: number;
    lastSignedAt: string;
    firstSignedAt: string;
    from1C?: boolean; // Флаг для компаний из 1С
}

interface Contract {
    id: number;
    contractNumber: string;
    companyName: string;
    companyBin: string;
    status: string;
    signedAt: string;
    createdAt: string;
    filePath?: string;
    company?: {
        id: number;
        bin: string;
        name: string;
    };
}

interface ContractStats {
    totalContracts: number;
    signedContracts: number;
    draftContracts: number;
    pendingContracts: number;
    companiesWithSignedContracts: number;
    signedLast30Days: number;
}

export const AdminContractsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [stats, setStats] = useState<ContractStats | null>(null);
    const [activeTab, setActiveTab] = useState('companies');
    const navigate = useNavigate();

    // Поиск
    const [searchTerm, setSearchTerm] = useState('');
    const [contractSearchTerm, setContractSearchTerm] = useState('');

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (activeTab === 'companies') {
            loadCompanies();
        } else if (activeTab === 'contracts') {
            loadSignedContracts();
        }
    }, [activeTab, searchTerm, contractSearchTerm]);

    const loadInitialData = async () => {
        await Promise.all([
            loadStats(),
            loadCompanies(),
            loadSignedContracts()
        ]);
    };

    const loadStats = async () => {
        try {
            const response = await adminApi.getContractStats();
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error: any) {
            console.error('Ошибка загрузки статистики:', error);
        }
    };

    const loadCompanies = async () => {
        setLoading(true);
        try {
            const response = await adminApi.getCompaniesWithSignedContracts(0, 50, searchTerm);
            if (response.data.success && response.data.data) {
                setCompanies(response.data.data);
            }
        } catch (error: any) {
            message.error('Ошибка загрузки компаний');
            console.error('Ошибка загрузки компаний:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSignedContracts = async () => {
        setLoading(true);
        try {
            const response = await adminApi.getSignedContracts(0, 100);
            if (response.data.success && response.data.data) {
                setContracts(response.data.data);
            }
        } catch (error: any) {
            message.error('Ошибка загрузки договоров');
            console.error('Ошибка загрузки договоров:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewCompany = (company: Company) => {
        navigate(`/admin/company-contracts/${company.id}`);
    };

    const handleViewContracts = (company: Company) => {
        // Переходим на страницу договоров компании
        navigate(`/admin/company-contracts/${company.id}`);
    };

    const handleDownloadContract = async (contractId: number) => {
        try {
            const response = await adminApi.downloadContract(contractId);
            if (response.data.success && response.data.data) {
                // Получаем S3 ссылку из ответа
                const downloadUrl = response.data.data.downloadUrl;

                // Открываем ссылку в новом окне для скачивания
                window.open(downloadUrl, '_blank');
            } else {
                message.error(response.data.message || 'Ошибка получения ссылки для скачивания');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Ошибка скачивания договора';
            message.error(errorMessage);
            console.error('Ошибка скачивания договора:', error);
        }
    };

    const getStatusColor = (status: string) => {
        const colors: { [key: string]: string } = {
            'SIGNED_BY_BUYER': 'green',
            'SIGNED_BY_SUPPLIER': 'blue',
            'SIGNED_BY_BOTH': 'purple',
            'PENDING_SIGNATURE': 'orange',
            'DRAFT': 'gray',
            'CANCELLED': 'red',
            'APPROVED': 'green',
            'PENDING_MODERATION': 'orange',
            'NEEDS_REVISION': 'red'
        };
        return colors[status] || 'default';
    };

    const getStatusText = (status: string) => {
        const texts: { [key: string]: string } = {
            'SIGNED_BY_BUYER': 'Подписан покупателем',
            'SIGNED_BY_SUPPLIER': 'Подписан поставщиком',
            'SIGNED_BY_BOTH': 'Подписан обеими сторонами',
            'PENDING_SIGNATURE': 'Ожидает подписания',
            'DRAFT': 'Черновик',
            'CANCELLED': 'Отменен',
            'APPROVED': 'Одобрен',
            'PENDING_MODERATION': 'На модерации',
            'NEEDS_REVISION': 'Требует доработки'
        };
        return texts[status] || status;
    };

    const companiesColumns = [
        {
            title: 'Компания',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: Company) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text strong>{text}</Text>
                        {record.from1C && (
                            <Tag color="blue" icon={<CloudSyncOutlined />}>
                                1С
                            </Tag>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        БИН: {record.bin}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Первый договор',
            dataIndex: 'firstSignedAt',
            key: 'firstSignedAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY')}
                </div>
            ),
        },
        {
            title: 'Последний договор',
            dataIndex: 'lastSignedAt',
            key: 'lastSignedAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY')}
                </div>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Company) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewCompany(record)}
                    >
                        Подробнее
                    </Button>
                    <Button
                        type="default"
                        icon={<FileTextOutlined />}
                        onClick={() => handleViewContracts(record)}
                    >
                        Договоры
                    </Button>
                </Space>
            ),
        },
    ];

    const contractsColumns = [
        {
            title: 'Номер договора',
            dataIndex: 'contractNumber',
            key: 'contractNumber',
            render: (text: string) => (
                <Text strong>{text}</Text>
            ),
        },
        {
            title: 'Компания',
            dataIndex: 'companyName',
            key: 'companyName',
            render: (text: string, record: Contract) => (
                <div>
                    <Text>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        БИН: {record.companyBin}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Дата создания',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY HH:mm')}
                </div>
            ),
        },
        {
            title: 'Дата подписания',
            dataIndex: 'signedAt',
            key: 'signedAt',
            render: (date: string) => (
                <div>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(date).format('DD.MM.YYYY HH:mm')}
                </div>
            ),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)} icon={<CheckCircleOutlined />}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Contract) => (
                <Space>
                    <Button
                        type="default"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadContract(record.id)}
                    >
                        Скачать
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>
                <FileTextOutlined style={{ marginRight: '8px' }} />
                Договоры клиентов
            </Title>

            {/* Статистика */}
            {stats && (
                <Card style={{ marginBottom: '24px' }}>
                    <Title level={4}>Общая статистика</Title>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Statistic
                                title="Всего договоров"
                                value={stats.totalContracts}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Подписанные"
                                value={stats.signedContracts}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Черновики"
                                value={stats.draftContracts}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Ожидают подписания"
                                value={stats.pendingContracts}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Компании с договорами"
                                value={stats.companiesWithSignedContracts}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Statistic
                                title="Подписано за 30 дней"
                                value={stats.signedLast30Days}
                                valueStyle={{ color: '#13c2c2' }}
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Основной контент */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="Компании с договорами" key="companies">
                        <div style={{ marginBottom: '16px' }}>
                            <Input
                                placeholder="Поиск по названию или БИН компании..."
                                prefix={<SearchOutlined />}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: '300px', marginRight: '8px' }}
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadCompanies}
                                loading={loading}
                            >
                                Обновить
                            </Button>
                        </div>

                        <Table
                            columns={companiesColumns}
                            dataSource={companies}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 15,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total) => `Всего: ${total} компаний`,
                            }}
                        />
                    </TabPane>

                    <TabPane tab="Все подписанные договоры" key="contracts">
                        <div style={{ marginBottom: '16px' }}>
                            <Input
                                placeholder="Поиск по номеру договора..."
                                prefix={<SearchOutlined />}
                                value={contractSearchTerm}
                                onChange={(e) => setContractSearchTerm(e.target.value)}
                                style={{ width: '300px', marginRight: '8px' }}
                            />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadSignedContracts}
                                loading={loading}
                            >
                                Обновить
                            </Button>
                        </div>

                        <Table
                            columns={contractsColumns}
                            dataSource={contracts}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 15,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total) => `Всего: ${total} договоров`,
                            }}
                        />
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
};